/**
 * Reseller ACCOUNTS — the contract between the command center's
 * "System Admin ▸ Reseller Accounts" surface and the two services that
 * together make a reseller login work.
 *
 * A reseller user is a TWO-SIDED record, and neither half is sufficient:
 *
 *   - **crm-api** holds the `ResellerMembership` — the authorization binding.
 *     Which reseller this human belongs to, and with what role. Keyed on
 *     EMAIL + tenant, because a portal-api user id does not exist in crm-api's
 *     users table.
 *   - **portal-api** holds the `User` — the identity. Password, passkey,
 *     invitation token. Role `reseller`, which is below every staff tier.
 *
 * The write order is **crm-api first, portal-api second**, and that is a
 * security property rather than a preference: a membership with no account is
 * inert (nothing can authenticate into it), while an account with no membership
 * authenticates and then fails resolution — a 401, not an unscoped session. So
 * a partial failure DENIES access. This module's `ResellerMemberStatus` exists
 * to make the surviving half of a partial failure VISIBLE rather than silent.
 *
 * Spec: docs/superpowers/specs/2026-08-28-reseller-identity-and-scope-wall-design.md
 * §3.3 and §6 (in the xop-resellers repo).
 */

/** The four BUILT-IN roles a reseller membership can carry (spec F §3).
 * `owner` is everything and cannot be narrowed; the other three carry a
 * default bundle the company may edit, plus per-person overrides. A company
 * may also have CUSTOM roles (2026-09-15) — see `ResellerRoleKey` — which is
 * why a membership's `role` is wider than `ResellerMemberRole`. Reseller
 * applications still link with a built-in only. Re-exported from the catalog
 * module so the two lists cannot disagree. */
export { RESELLER_ROLES as RESELLER_MEMBER_ROLES, RESELLER_ROLE_LABELS as RESELLER_MEMBER_ROLE_LABELS } from "./resellerPermissions.js";
export type { ResellerRole as ResellerMemberRole } from "./resellerPermissions.js";
import type { ResellerRoleKey } from "./resellerPermissions.js";

/** crm-api's `ResellerMembership` row — the authorization binding. */
export interface ResellerMembership {
  id: number;
  reseller_id: number;
  email: string;
  /** A built-in role or one of this company's custom keys (`custom_…`). */
  role: ResellerRoleKey;
  /** crm-api's display label for `role` — present for custom roles on a
   * crm-api that has them; absent on an older one. */
  role_label?: string | null;
  active: boolean;
  last_seen_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: { id: number; full_name: string; email: string } | null;
  /** Spec F §7.2 — per-person exceptions and the computed effective set. */
  permission_grants: string[];
  permission_denies: string[];
  effective_permissions: string[];
}

/** portal-api's `User` row for a reseller, as `Admin::UserSerializer` renders
 * it. Only the fields this surface actually reads. */
export interface ResellerPortalAccount {
  id: number;
  email: string;
  full_name: string | null;
  role: string;
  active: boolean;
  last_sign_in_at: string | null;
}

/**
 * What the operator needs to know about one row, derived from both halves.
 *
 * `orphaned_membership` and `orphaned_account` are the two shapes a partial
 * two-sided write leaves behind. Neither grants access — that is the point of
 * the ordering — but both are states a human has to resolve, so they get their
 * own verdicts instead of being flattened into "invited".
 */
export type ResellerMemberStatus =
  /** Both halves present, the person has signed in at least once. */
  | "active"
  /** Both halves present, never signed in — the invite is outstanding. */
  | "invited"
  /** Membership active, portal-api account deactivated. Cannot sign in. */
  | "account_disabled"
  /** Membership active, NO portal-api account. The person cannot sign in;
   *  the crm-api half of the two-sided write landed and the portal-api half
   *  did not. Fix by re-sending the invite. */
  | "orphaned_membership"
  /** Membership revoked. CRM access is denied within one token TTL. */
  | "revoked";

/** A reseller-role portal-api account with no matching crm-api membership in
 * this reseller. It would authenticate and then fail resolution (401), so it
 * grants nothing — but it is still a loose end the roster surfaces. */
export interface OrphanedResellerAccount {
  account: ResellerPortalAccount;
}

/** One roster row: the binding, the identity, and the verdict. */
export interface ResellerMember {
  membership: ResellerMembership;
  account: ResellerPortalAccount | null;
  status: ResellerMemberStatus;
}

export interface ListResellerMembersResponse {
  members: ResellerMember[];
  /** Reseller-role portal-api accounts that NO membership of this reseller
   * claims — active or revoked. A revoked member whose login still exists is
   * reported on its own row instead, so it does not appear twice. Since
   * 2026-11-01 only sign-ins with NO membership at ANY reseller, and only for a
   * holder of `admin:users` — another company's users never appear here. Empty
   * in the healthy case. */
  orphaned_accounts: OrphanedResellerAccount[];
  /** False when portal-api could not be reached, so the UI can say "identity
   * side unavailable" instead of silently reporting every row orphaned —
   * an unreachable service is not an orphaned account. */
  identity_available: boolean;
}

/** The result of the two-sided create. `membership` is always present when the
 * call succeeded at all (crm-api goes first); `invited` says whether the
 * portal-api half also landed. */
export interface InviteResellerMemberResponse {
  member: ResellerMember;
  /** True when portal-api created/re-invited the account AND sent the email. */
  invited: boolean;
  /** Present only when `invited` is false — why the identity half did not
   * land, so the operator gets a reason rather than a silent half-write. */
  invite_error?: string;
  /**
   * portal-api's machine-readable reason, when it gave one. Carried
   * separately from `invite_error` because not every half-write is
   * retryable, and the UI must not offer "Re-send invite" for one that
   * isn't: `EMAIL_BELONGS_TO_STAFF` means the address is already an XOP
   * staff identity, which no number of retries will change.
   */
  invite_error_code?: string;
}

/** portal-api refused because the address is already an XOP staff account.
 * A PERMANENT conflict — one address cannot be both staff and an external
 * reseller — so the fix is a different address, never a re-send. */
export const INVITE_ERROR_EMAIL_IS_STAFF = "EMAIL_BELONGS_TO_STAFF";

export interface RevokeResellerMemberResponse {
  member: ResellerMember;
  /** True when the portal-api account was also deactivated. False means only
   * the CRM binding was revoked — still enough to deny CRM access within one
   * token TTL, but the identity survives and should be retried. */
  account_disabled: boolean;
  account_error?: string;
}

/**
 * DELETE /api/admin/resellers/:id/members/:membershipId — "Delete user"
 * (2026-11-01). The membership is gone (crm-api, first); `account_deleted`
 * says whether the portal-api sign-in was deleted too (second). False means
 * the person can no longer reach any CRM data, but a sign-in survives — the
 * roster then lists it under "Reseller sign-ins with no binding here".
 */
export interface DeleteResellerMemberResponse {
  deleted: true;
  email: string;
  account_deleted: boolean;
  account_error?: string;
}

/**
 * The single definition of a roster row's verdict. Both the server (building
 * the response) and any client that wants to re-derive it use THIS — two
 * implementations that can disagree about whether an account is orphaned is
 * exactly the drift this surface exists to catch.
 */
export function resellerMemberStatus(
  membership: Pick<ResellerMembership, "active">,
  account: Pick<ResellerPortalAccount, "active" | "last_sign_in_at"> | null,
): ResellerMemberStatus {
  // Revoked wins over everything: once the binding is off, what the identity
  // half looks like no longer changes what the person can reach in the CRM.
  if (!membership.active) return "revoked";
  if (!account) return "orphaned_membership";
  if (!account.active) return "account_disabled";
  return account.last_sign_in_at ? "active" : "invited";
}

export const RESELLER_MEMBER_STATUS_LABELS: Record<ResellerMemberStatus, string> = {
  active: "Active",
  invited: "Invited",
  account_disabled: "Sign-in disabled",
  orphaned_membership: "No account",
  revoked: "Revoked",
};

/** Short sentence explaining a verdict, shown as the row's tooltip. Written for
 * an operator deciding what to DO, not as a restatement of the label. */
export const RESELLER_MEMBER_STATUS_HINTS: Record<ResellerMemberStatus, string> = {
  active: "Has a reseller account and has signed in.",
  invited: "Invite sent — they haven't set a password yet. Re-send if it was missed.",
  account_disabled:
    "The CRM binding is live but the sign-in account is deactivated, so they cannot log in.",
  orphaned_membership:
    "The CRM binding exists but no sign-in account does — the invite half of the write did not land. Re-send the invite.",
  revoked: "Access removed. CRM access stops within one token lifetime.",
};

/** Whether a status warrants operator attention on the roster. Drives the
 * warning chip, so it is deliberately narrow: "invited" is a normal waiting
 * state, not a problem. */
export function resellerMemberNeedsAttention(status: ResellerMemberStatus): boolean {
  return status === "orphaned_membership" || status === "account_disabled";
}

/* `ResellerRoleMatrix` (crm-api's `GET /api/v1/resellers/:id/role_permissions`)
 * lives in `resellerPermissions.ts` beside `matrixRows`, which reads it. */

/** crm-api's `GET /api/v1/reseller-permissions/catalog`, forwarded verbatim. */
export interface PermissionCatalog {
  sections: Record<string, string>;
  verbs: string[];
  keys: string[];
  grantable: string[];
  implications: Record<string, string[]>;
  roles: string[];
  editable_roles: string[];
  defaults: Record<string, string[]>;
}

/** `PATCH /api/admin/resellers/:id/members/:membershipId` — at least one key. */
export interface SetResellerMemberPermissionsRequest {
  role?: ResellerRoleKey;
  permission_grants?: string[];
  permission_denies?: string[];
}
